import { Button, Card, Descriptions, Spin, Tag } from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftOutlined, ExperimentOutlined } from "@ant-design/icons";
import DataProvider from "../../apis";
import type { DiagnosticItem } from "../../apis";
import Colors from "../../constants/Colors";
import {
  E_PRESCRIPTIONS_BASE,
  diagnosticStatusConfig as statusConfig,
} from "../../constants/e-prescriptions";

export default function DiagnosticDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<DiagnosticItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    DataProvider.getDiagnosticById(Number(id))
      .then((res) => setData(res.data))
      .catch(() => setError("Could not load this lab request."))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh] bg-[#f8fafc]">
        <Spin size="large" tip="Loading lab request…" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-[50vh] bg-[#f8fafc] flex flex-col items-center justify-center p-6">
        <p className="text-red-600 mb-4 text-center">{error ?? "Lab request not found."}</p>
        <Button
          type="primary"
          onClick={() => navigate(`${E_PRESCRIPTIONS_BASE}?tab=2`)}
          style={{ background: Colors.primary }}
          aria-label="Back to lab requests"
        >
          Back to lab requests
        </Button>
      </div>
    );
  }

  const status = statusConfig[data.status?.toLowerCase()] ?? {
    color: "#94a3b8",
    label: data.status?.replace(/_/g, " ") ?? "—",
  };

  return (
    <div className="min-h-[70vh] bg-[#f8fafc] pb-12">
      <div className="max-w-2xl mx-auto px-4 pt-4">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`${E_PRESCRIPTIONS_BASE}?tab=2`)}
          className="mb-2"
          aria-label="Back to lab requests"
        >
          Back
        </Button>
        <Card className="rounded-xl shadow-sm">
          <div className="flex items-center gap-2 mb-6">
            <ExperimentOutlined className="text-2xl" style={{ color: Colors.primary }} aria-hidden />
            <h1 className="text-xl font-semibold m-0">Lab request #{data.orderId}</h1>
          </div>
          <Tag color={status.color} className="mb-4">
            {status.label}
          </Tag>
          <Descriptions column={1} bordered size="small" className="diagnostic-details">
            <Descriptions.Item label="Patient">{data.fullname ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="Contact">{data.contact ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="Scheduled date">
              {data.schedule_date
                ? `${new Date(data.schedule_date).toLocaleDateString()}${data.schedule_time ? ` ${data.schedule_time}` : ""}`
                : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Payment status">
              {data.paymentStatus?.replace(/_/g, " ") ?? "—"}
            </Descriptions.Item>
            {data.notes && (
              <Descriptions.Item label="Notes">{data.notes}</Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      </div>
    </div>
  );
}
